import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Upload, Camera, Video, CheckCircle, Loader2 } from "lucide-react";
import UploadZone from "@/components/UploadZone";
import { savePhoto } from "@/services/photoStorage";
import { useToast } from "@/hooks/use-toast";
import { triggerUgcEmail } from "@/services/ugcEmailTrigger";

const examples = [
  {
    id: 1,
    src: "https://res.cloudinary.com/dxn6haxtz/image/upload/v1743212252/ChatGPT_Image_Mar_28_2025_06_32_58_PM_ypo6hz.png",
    title: "Using it IRL",
    description: "Capture your product being used in its natural environment. This helps others see how it fits into their life.",
  },
  {
    id: 2,
    src: "https://res.cloudinary.com/dxn6haxtz/image/upload/v1743211988/ChatGPT_Image_Mar_28_2025_06_27_36_PM_zjjbnb.png",
    title: "Natural lighting is key",
    description: "Take photos in well-lit areas, preferably with natural light. This brings out the best in your products!",
  },
  {
    id: 3,
    src: "https://res.cloudinary.com/dxn6haxtz/image/upload/v1743207987/ce1mul94ff2mnphykziv.png",
    title: "Show in your Lifestyle",
    description: "Include some context in your shots. Show how the product enhances your daily life!",
  },
];

const steps = [
  { id: 'examples', label: 'Examples' },
  { id: 'method', label: 'Choose Method' },
  { id: 'upload', label: 'Upload' },
  { id: 'details', label: 'Details' },
  { id: 'success', label: 'Done' }
] as const;

type Step = typeof steps[number]['id'];

interface GuidedUploadProps {
  onClose: () => void;
  onSubmitSuccess: () => void;
  brand: { id: string; name: string };
}

const styles = `
  @keyframes slowPing {
    0% {
      transform: scale(1);
      opacity: 0.25;
    }
    50% {
      transform: scale(1.5);
      opacity: 0.15;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
  }
`;

export default function GuidedUpload({ onClose, onSubmitSuccess, brand }: GuidedUploadProps) {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<Step>('examples');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const nextExample = () => {
    if (currentIndex === examples.length - 1) {
      setCurrentStep('method');
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevExample = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setCurrentStep('details');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !name || !email || !brand) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const newPhoto = {
        id: crypto.randomUUID(),
        customerName: name,
        customerEmail: email,
        productName: "",
        submissionDate: new Date().toISOString(),
        status: "pending",
        brandId: brand.id,
      };

      // Save the photo and customer info
      await savePhoto(newPhoto, selectedFile);

      // Show success step
      setCurrentStep('success');

      // Trigger UGC email asynchronously after successful submission of photo AND customer info
      triggerUgcEmail(brand.id).catch(error => {
        // Silently handle UGC email errors - don't show to user
        console.error('UGC email error:', error);
      });
    } catch (error) {
      console.error('Error in submission:', error);
      toast({
        title: "Error",
        description: "Failed to save your submission. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Simply close the modal
    onSubmitSuccess();
  };

  const renderProgressBar = () => (
    <div className="mb-8 md:mb-12">
      <div className="flex justify-between mb-2 px-1">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`text-[10px] sm:text-xs md:text-sm font-medium ${index <= currentStepIndex ? 'text-primary' : 'text-muted-foreground'
              }`}
          >
            {step.label}
          </div>
        ))}
      </div>
      <div className="w-full h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300 ease-in-out rounded-full"
          style={{
            width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
          }}
        />
      </div>
    </div>
  );

  const renderExamplesStep = () => (
    <div className="space-y-6 md:space-y-8">
      <div className="aspect-[16/10] relative overflow-hidden rounded-lg">
        <img
          src={examples[currentIndex].src}
          alt={examples[currentIndex].title}
          className="w-full h-full object-cover object-[center_15%]"
        />
      </div>

      <div className="space-y-3 md:space-y-4 text-center px-2 sm:px-4">
        <h3 className="text-xl md:text-2xl font-semibold">{examples[currentIndex].title}</h3>
        <p className="text-base md:text-lg text-muted-foreground">{examples[currentIndex].description}</p>
      </div>

      <div className="flex items-center justify-between gap-3 sm:gap-4">
        <Button
          variant="outline"
          onClick={prevExample}
          disabled={currentIndex === 0}
          className="w-[100px] md:w-[120px]"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Button onClick={nextExample} className="w-[100px] md:w-[120px]">
          {currentIndex === examples.length - 1 ? "Get Started!" : (
            <>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderMethodStep = () => (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-semibold mb-2">How would you like to share?</h2>
        <p className="text-sm md:text-base text-muted-foreground">Choose your preferred way to share your content</p>
      </div>

      <div className="flex flex-col space-y-3">
        <Button
          variant="secondary"
          className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
          onClick={() => setCurrentStep('upload')}
        >
          <Upload className="h-6 w-6" />
          Upload Photo/Video
        </Button>

        <Button
          variant="secondary"
          className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
          onClick={() => {
            if (cameraInputRef.current) {
              cameraInputRef.current.click();
            }
          }}
        >
          <Camera className="h-6 w-6" />
          Take Photo
        </Button>

        <Button
          variant="secondary"
          className="bg-black text-white py-6 rounded-lg text-lg font-medium hover:bg-gray-800 w-full flex items-center justify-center gap-2"
          onClick={() => {
            if (videoInputRef.current) {
              videoInputRef.current.click();
            }
          }}
        >
          <Video className="h-6 w-6" />
          Record Video
        </Button>

        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
          className="hidden"
        />

        <input
          type="file"
          ref={videoInputRef}
          onChange={handleFileChange}
          accept="video/*"
          capture="environment"
          className="hidden"
        />
      </div>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => {
            setCurrentStep('examples');
            setCurrentIndex(examples.length - 1);
          }}
          className="w-[100px] md:w-[120px]"
        >
          Back
        </Button>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">Upload Your Content</h2>
        <p className="text-muted-foreground">Drag and drop your photo or video, or click to browse</p>
      </div>

      <UploadZone onFileSelected={handleFileChange} />

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep('method')}
          className="w-[120px]"
        >
          Back
        </Button>
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">You're so close to earning $25!</h2>
        <p className="text-muted-foreground">Just fill in your details below to complete your submission</p>
      </div>

      <div className="space-y-6 max-w-md mx-auto">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            placeholder="Enter your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="transition-all focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="transition-all focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground">We'll send your reward to this email</p>
        </div>
      </div>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep('upload')}
          className="w-[120px]"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="w-[200px]"
          disabled={!name || !email || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Complete Submission"
          )}
        </Button>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="space-y-12 text-center py-8">
      <div className="relative">
        <CheckCircle className="h-32 w-32 mx-auto text-primary" />
        <div className="absolute inset-0">
          <div
            className="bg-gradient-to-r from-primary to-primary/50 rounded-full h-32 w-32 mx-auto"
            style={{
              animation: 'slowPing 1.5s cubic-bezier(0, 0, 0.2, 1) 3',
              animationFillMode: 'forwards',
              opacity: 0.25
            }}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-3xl font-semibold">Amazing job!</h2>
        <div className="space-y-2 max-w-md mx-auto">
          <p className="text-muted-foreground text-lg">
            Once your content is approved by the brand, you'll receive your $25 reward via email.
          </p>
          <p className="text-muted-foreground">
            Keep an eye on your inbox!
          </p>
        </div>
      </div>

      <div className="pt-8">
        <Button
          onClick={handleClose}
          className="w-[200px]"
        >
          Close
        </Button>
      </div>
    </div>
  );

  const stepContent = {
    examples: renderExamplesStep,
    method: renderMethodStep,
    upload: renderUploadStep,
    details: renderDetailsStep,
    success: renderSuccessStep,
  };

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto py-6 md:py-12 px-4 sm:px-6">
          <div className="space-y-6 md:space-y-8">
            <div className="text-center">
              <img
                src={"https://res.cloudinary.com/dxn6haxtz/image/upload/v1743212179/CleanShot_2025-03-28_at_19.36.04_exh8h1.png"}
                alt="Brand Logo"
                className="max-w-[160px] md:max-w-[200px] h-auto mx-auto mb-6 md:mb-8"
              />
            </div>
            {renderProgressBar()}
            <div className="bg-card rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
              {stepContent[currentStep]()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 